import React, { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../amplify/data/resource';
import StatusMessage from '../components/StatusMessage';


interface Course {
  id: string;
  title: string;
  startDate?: string;
  endDate?: string;
  location?: string;
  isActive: boolean;
}

interface Registration {
  id: string;
  courseId: string;
  firstName: string;
  lastName: string;
  email: string;
  company?: string;
  phone?: string;
  registrationDate: string;
  status: string;
  notes?: string;
  course?: Course;
  // Registrant information (person who made the registration)
  registrantFirstName?: string;
  registrantLastName?: string;
  registrantEmail?: string;
  registrantPhone?: string;
}

type SortField = 'registrationDate' | 'firstName' | 'lastName' | 'company' | 'email' | 'phone' | 'status';
type SortDirection = 'asc' | 'desc';

interface AdminRegistrationsProps {
  onEditRegistration?: (registrationId: string) => void;
}

const AdminRegistrations: React.FC<AdminRegistrationsProps> = ({ onEditRegistration }) => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning' | 'info'; text: string } | null>(null);
  const [sortField, setSortField] = useState<SortField>('registrationDate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [showArchivedRegistrations, setShowArchivedRegistrations] = useState<boolean>(false);
  const [showCancelledRegistrations, setShowCancelledRegistrations] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [archivedCurrentPage, setArchivedCurrentPage] = useState(1);
  const [archivedItemsPerPage] = useState(10);
  const [cancelledCurrentPage, setCancelledCurrentPage] = useState(1);
  const [cancelledItemsPerPage] = useState(10);

  useEffect(() => {
    fetchData();
  }, []);

  // Reset pagination when course, status, or search filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCourse, selectedStatus, searchTerm]);

  // Inject course and status selectors into header
  useEffect(() => {
    const container = document.getElementById('header-selector-container');
    if (container) {
      // Only recreate the HTML if courses have changed or if the container is empty
      const existingSearchInput = container.querySelector('#searchInput');
      const shouldRecreate = !existingSearchInput || courses.length !== container.querySelectorAll('option').length - 6; // -6 for the 6 status options
      
      if (shouldRecreate) {
        container.innerHTML = `
          <div style="display: flex; align-items: center; gap: 12px;">
            <select
              id="courseSelect"
              value="${selectedCourse}"
              style="padding: 8px 12px; border: 1px solid rgba(255, 255, 255, 0.3); border-radius: 6px; font-size: 14px; min-width: 200px; background: rgba(255, 255, 255, 0.95); color: #374151; box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);"
            >
              <option value="all">All Courses</option>
              ${courses.map(course => 
                `<option value="${course.id}">${course.title}${course.location ? ` - ${course.location}` : ''}</option>`
              ).join('')}
            </select>
            <select
              id="statusSelect"
              value="${selectedStatus}"
              style="padding: 8px 12px; border: 1px solid rgba(255, 255, 255, 0.3); border-radius: 6px; font-size: 14px; min-width: 120px; background: rgba(255, 255, 255, 0.95); color: #374151; box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);"
            >
              <option value="all">All Status</option>
              <option value="open">Open</option>
              <option value="invoiced">Invoiced</option>
              <option value="paid">Paid</option>
              <option value="cancelled">Cancelled</option>
              <option value="alumni">Alumni</option>
            </select>
            <input
              type="text"
              id="searchInput"
              placeholder="Search participants..."
              style="padding: 8px 12px; border: 1px solid rgba(255, 255, 255, 0.3); border-radius: 6px; font-size: 14px; min-width: 200px; background: rgba(255, 255, 255, 0.95); color: #374151; box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);"
            />
          </div>
        `;
      }

      // Add event listeners and set current values
      const courseSelect = container.querySelector('#courseSelect') as HTMLSelectElement;
      const statusSelect = container.querySelector('#statusSelect') as HTMLSelectElement;
      const searchInput = container.querySelector('#searchInput') as HTMLInputElement;
      
      if (courseSelect) {
        // Set the current selected value
        courseSelect.value = selectedCourse;
        
        // Add event listener (will be cleaned up when DOM is recreated)
        courseSelect.addEventListener('change', (e: Event) => {
          setSelectedCourse((e.target as HTMLSelectElement).value);
        });
      }
      
      if (statusSelect) {
        // Set the current selected value
        statusSelect.value = selectedStatus;
        
        // Add event listener (will be cleaned up when DOM is recreated)
        statusSelect.addEventListener('change', (e: Event) => {
          setSelectedStatus((e.target as HTMLSelectElement).value);
        });
      }
      
      if (searchInput) {
        // Only set the value if it's different to avoid cursor jumping
        if (searchInput.value !== searchTerm) {
          searchInput.value = searchTerm;
        }
        
        // Add event listener (will be cleaned up when DOM is recreated)
        searchInput.addEventListener('input', (e: Event) => {
          setSearchTerm((e.target as HTMLInputElement).value);
        });
      }

      // Expose refresh function globally
      (window as any).adminRegistrationsRefresh = fetchData;
    }

    return () => {
      // Cleanup
      if (container) {
        container.innerHTML = '';
      }
      delete (window as any).adminRegistrationsRefresh;
    };
  }, [selectedCourse, selectedStatus, courses]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const client = generateClient<Schema>({ authMode: 'userPool' });

      // Fetch data - Amplify handles environment isolation automatically

      // Fetch courses
      const { data: coursesData } = await client.models.Course.list({
        filter: {
          isActive: { eq: true }
        }
      });

      setCourses((coursesData || []) as Course[]);

      // Fetch registrations (include all; exclude only those explicitly marked as deleted)
      // Fetch ALL registrations with pagination to handle large datasets
      let allRegistrations: any[] = [];
      let nextToken: string | null | undefined = null;
      
      do {
        const response: any = await client.models.Registration.list({
          limit: 1000,
          nextToken: nextToken as any
        });
        
        allRegistrations = [...allRegistrations, ...(response.data || [])];
        nextToken = response.nextToken;
        
        console.log(`📊 Fetched ${response.data?.length || 0} registrations (total so far: ${allRegistrations.length})`);
      } while (nextToken);
      
      console.log(`✅ Total registrations fetched: ${allRegistrations.length}`);
      
      const nonDeleted = allRegistrations.filter((reg: any) => reg?.status !== 'deleted');
      setRegistrations(nonDeleted as Registration[]);
      setError(null);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load registration data');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (registrationId: string, newStatus: string) => {
    try {
      const client = generateClient<Schema>({ authMode: 'userPool' });
      await client.models.Registration.update({
        id: registrationId,
        status: newStatus
      });
      
      // Update local state
      setRegistrations(prev => 
        prev.map(reg => 
          reg.id === registrationId 
            ? { ...reg, status: newStatus }
            : reg
        )
      );
      
      console.log(`✅ Registration status updated to ${newStatus}`);
    } catch (err) {
      console.error('Error updating registration status:', err);
      setMessage({ type: 'error', text: 'Failed to update registration status' });
    }
  };

    const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredRegistrations = registrations.filter(reg => {
    const matchesCourse = selectedCourse === 'all' || reg.courseId === selectedCourse;
    const matchesStatus = selectedStatus === 'all' || reg.status === selectedStatus;
    
    // Search functionality - search in firstName, lastName, email, company
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = searchTerm === '' || 
      reg.firstName?.toLowerCase().includes(searchLower) ||
      reg.lastName?.toLowerCase().includes(searchLower) ||
      reg.email?.toLowerCase().includes(searchLower) ||
      reg.company?.toLowerCase().includes(searchLower);
    
    return matchesCourse && matchesStatus && matchesSearch;
  });

  // Show all registrations in the main table, separate archived and cancelled for their own sections
  const activeRegistrations = filteredRegistrations.filter(reg => 
    reg.status !== 'alumni' && reg.status !== 'cancelled'
  );
  const archivedRegistrations = filteredRegistrations.filter(reg => 
    reg.status === 'alumni'
  );
  const cancelledRegistrations = filteredRegistrations.filter(reg => 
    reg.status === 'cancelled'
  );

  // Sort function for registrations
  const sortRegistrations = (regsToSort: Registration[]) => {
    return [...regsToSort].sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];

      // Handle date sorting
      if (sortField === 'registrationDate') {
        aValue = aValue ? new Date(aValue).getTime() : 0;
        bValue = bValue ? new Date(bValue).getTime() : 0;
      }

      // Handle string sorting
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      // Handle null/undefined values
      if (!aValue && !bValue) return 0;
      if (!aValue) return sortDirection === 'asc' ? 1 : -1;
      if (!bValue) return sortDirection === 'asc' ? -1 : 1;

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Sort active, archived, and cancelled registrations
  const sortedActiveRegistrations = sortRegistrations(activeRegistrations);
  const sortedArchivedRegistrations = sortRegistrations(archivedRegistrations);
  const sortedCancelledRegistrations = sortRegistrations(cancelledRegistrations);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return { bg: '#fef3c7', color: '#92400e' }; // Light yellow for open/pending
      case 'pending': return { bg: '#fef3c7', color: '#92400e' }; // Light yellow for pending
      case 'confirmed': return { bg: '#e6f7f7', color: '#008F99' }; // Brand color for confirmed
      case 'invoiced': return { bg: '#dbeafe', color: '#1e40af' }; // Blue for invoiced
      case 'paid': return { bg: '#d1fae5', color: '#065f46' }; // Green for paid
      case 'cancelled': return { bg: '#fee2e2', color: '#991b1b' }; // Red for cancelled
      case 'alumni': return { bg: '#e0e7ff', color: '#3730a3' }; // Purple for alumni
      default: return { bg: '#f3f4f6', color: '#374151' }; // Gray for unknown
    }
  };

  const getStatusDisplayText = (status: string) => {
    return status === 'confirmed' ? 'Paid' : status;
  };

  if (loading) {
    return (
      <div className="admin-content-card">
        <div className="admin-toolbar">
          <h2>Loading registrations...</h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-content-card">
        <div className="admin-toolbar">
          <h2>Error: {error}</h2>
          <button className="admin-btn primary" onClick={fetchData}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-content-card" style={{ paddingBottom: '60px' }}>
      {message && (
        <div style={{ padding: '16px' }}>
          <StatusMessage
            type={message.type}
            message={message.text}
            onClose={() => setMessage(null)}
          />
        </div>
      )}

      {/* Pagination logic */}
      {(() => {
        const totalItems = sortedActiveRegistrations.length;
        const totalPages = Math.ceil(totalItems / itemsPerPage);
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const paginatedRegistrations = sortedActiveRegistrations.slice(startIndex, endIndex);
        
        const handlePageChange = (page: number) => {
          setCurrentPage(page);
        };

        return (
          <>
            {sortedActiveRegistrations.length === 0 ? (
        <div style={{ padding: '48px 24px', textAlign: 'center', color: '#6b7280' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>👥</div>
          <h3 style={{ margin: '0 0 8px 0', color: '#374151' }}>No registrations yet</h3>
          <p style={{ margin: 0 }}>
            {selectedCourse === 'all' 
              ? 'No course registrations found. They will appear here when users register for courses.'
              : 'No registrations for the selected course.'}
          </p>
          <div style={{ 
            marginTop: '24px', 
            padding: '16px', 
            background: '#f0f9ff', 
            borderRadius: '8px',
            border: '1px solid #bfdbfe'
          }}>
            <p style={{ margin: 0, fontSize: '14px', color: '#1e40af' }}>
              💡 <strong>Ready for registrations!</strong> The Registration system is now implemented. 
              Course registrations will appear here when users register for courses through the public registration form.
            </p>
          </div>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="admin-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('registrationDate')} style={{ cursor: 'pointer' }}>
                  Date {sortField === 'registrationDate' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('firstName')} style={{ cursor: 'pointer' }}>
                  Participant {sortField === 'firstName' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th>Course</th>
                <th onClick={() => handleSort('company')} style={{ cursor: 'pointer' }}>
                  Company {sortField === 'company' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('email')} style={{ cursor: 'pointer' }}>
                  Email {sortField === 'email' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('phone')} style={{ cursor: 'pointer' }}>
                  Phone {sortField === 'phone' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>
                  Status {sortField === 'status' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedRegistrations.map((registration) => {
                const course = courses.find(c => c.id === registration.courseId);
                const statusStyle = getStatusColor(registration.status);
                
                return (
                  <tr key={registration.id} style={{ minHeight: '60px' }}>
                    <td style={{ verticalAlign: 'middle', padding: '12px 16px' }}>
                      <div style={{ 
                        fontSize: '12px', 
                        fontWeight: 500,
                        color: '#6b7280',
                        textAlign: 'center'
                      }}>
                        {new Date(registration.registrationDate).toLocaleDateString('de-DE', {
                          day: '2-digit',
                          month: '2-digit',
                          year: '2-digit'
                        })}
                      </div>
                    </td>
                    <td style={{ verticalAlign: 'middle', padding: '12px 16px' }}>
                      <div style={{ fontWeight: 600, color: '#1f2937' }}>
                        {registration.firstName} {registration.lastName}
                      </div>
                    </td>
                    <td style={{ verticalAlign: 'middle', padding: '12px 16px' }}>
                      <div>
                        <div style={{ fontWeight: 500 }}>
                          {course?.title || 'Unknown Course'}
                        </div>
                        {course?.startDate && (
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>
                            📅 {course.startDate} - {course.endDate}
                          </div>
                        )}
                        {course?.location && (
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>
                            📍 {course.location}
                          </div>
                        )}
                      </div>
                    </td>
                    <td style={{ verticalAlign: 'middle', padding: '12px 16px' }}>
                      <div>
                        <div style={{ fontWeight: 500 }}>
                          {registration.company || '-'}
                        </div>
                        {(registration.registrantFirstName || registration.registrantLastName) && (
                          <div style={{ 
                            fontSize: '12px', 
                            color: '#6b7280', 
                            marginTop: '2px' 
                          }}>
                            Registrant: {registration.registrantFirstName} {registration.registrantLastName}
                          </div>
                        )}
                      </div>
                    </td>
                    <td style={{ verticalAlign: 'middle', padding: '12px 16px' }}>
                      <div style={{ fontSize: '13px' }}>
                        {registration.email || '-'}
                      </div>
                    </td>
                    <td style={{ verticalAlign: 'middle', padding: '12px 16px' }}>
                      <div style={{ fontSize: '13px' }}>
                        {registration.phone || '-'}
                      </div>
                    </td>
                    <td style={{ verticalAlign: 'middle', padding: '12px 16px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        backgroundColor: statusStyle.bg,
                        color: statusStyle.color
                      }}>
                        {getStatusDisplayText(registration.status)}
                      </span>
                    </td>
                    <td style={{ verticalAlign: 'middle', padding: '12px 16px' }}>
                      <div className="actions" style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                        {/* First row: Edit and primary status actions */}
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                          {onEditRegistration && (
                            <button
                              onClick={() => onEditRegistration(registration.id)}
                              style={{
                                padding: '4px 8px',
                                border: '1px solid #008F99',
                                borderRadius: '4px',
                                background: 'white',
                                color: '#008F99',
                                fontSize: '11px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#008F99';
                                e.currentTarget.style.color = 'white';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'white';
                                e.currentTarget.style.color = '#008F99';
                              }}
                            >
                              Edit
                            </button>
                          )}
                          {registration.status === 'open' && (
                            <button
                              onClick={() => handleStatusChange(registration.id, 'invoiced')}
                              style={{
                                padding: '4px 8px',
                                border: '1px solid #008F99',
                                borderRadius: '4px',
                                background: 'white',
                                color: '#008F99',
                                fontSize: '11px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#008F99';
                                e.currentTarget.style.color = 'white';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'white';
                                e.currentTarget.style.color = '#008F99';
                              }}
                            >
                              Invoice
                            </button>
                          )}
                          {registration.status === 'invoiced' && (
                            <button
                              onClick={() => handleStatusChange(registration.id, 'paid')}
                              style={{
                                padding: '4px 8px',
                                border: '1px solid #008F99',
                                borderRadius: '4px',
                                background: 'white',
                                color: '#008F99',
                                fontSize: '11px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#008F99';
                                e.currentTarget.style.color = 'white';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'white';
                                e.currentTarget.style.color = '#008F99';
                              }}
                            >
                              Mark Paid
                            </button>
                          )}
                        </div>
                        
                        {/* Second row: Cancel and Alumni actions */}
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                          {registration.status !== 'cancelled' && registration.status !== 'alumni' && (
                            <button
                              onClick={() => handleStatusChange(registration.id, 'cancelled')}
                              style={{
                                padding: '4px 8px',
                                border: '1px solid #008F99',
                                borderRadius: '4px',
                                background: 'white',
                                color: '#008F99',
                                fontSize: '11px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#008F99';
                                e.currentTarget.style.color = 'white';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'white';
                                e.currentTarget.style.color = '#008F99';
                              }}
                            >
                              Cancel
                            </button>
                          )}
                          {(registration.status === 'open' || registration.status === 'invoiced' || registration.status === 'paid') && (
                            <button
                              onClick={() => handleStatusChange(registration.id, 'alumni')}
                              style={{
                                padding: '4px 8px',
                                border: '1px solid #6b7280',
                                borderRadius: '4px',
                                background: 'white',
                                color: '#6b7280',
                                fontSize: '11px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#6b7280';
                                e.currentTarget.style.color = 'white';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'white';
                                e.currentTarget.style.color = '#6b7280';
                              }}
                            >
                              Archive
                            </button>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '8px',
              marginTop: '20px',
              padding: '16px'
            }}>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  background: currentPage === 1 ? '#f9fafb' : 'white',
                  color: currentPage === 1 ? '#9ca3af' : '#374151',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                Previous
              </button>
              
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    background: currentPage === page ? '#008F99' : 'white',
                    color: currentPage === page ? 'white' : '#374151',
                    cursor: 'pointer',
                    fontSize: '14px',
                    minWidth: '40px'
                  }}
                >
                  {page}
                </button>
              ))}
              
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  background: currentPage === totalPages ? '#f9fafb' : 'white',
                  color: currentPage === totalPages ? '#9ca3af' : '#374151',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                Next
              </button>
              
              <div style={{ marginLeft: '16px', fontSize: '14px', color: '#6b7280' }}>
                Showing {startIndex + 1}-{Math.min(endIndex, totalItems)} of {totalItems} participants
              </div>
            </div>
          )}
        </div>
      )}
            </>
          );
        })()}
      

      {/* Archived Participants (Cancelled & Alumni) */}
      {archivedRegistrations.length > 0 && (
        <div style={{ marginTop: '24px' }}>
          <div 
            onClick={() => setShowArchivedRegistrations(!showArchivedRegistrations)}
            style={{
              padding: '12px 16px',
              background: '#f8f9fa',
              border: '1px solid #e5e7eb',
              borderRadius: '8px 8px 0 0',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontWeight: '600',
              color: '#374151'
            }}
          >
            <span>Archived Participants ({archivedRegistrations.length})</span>
            <span style={{ fontSize: '12px' }}>
              {showArchivedRegistrations ? '▼' : '▶'}
            </span>
          </div>
          
          {showArchivedRegistrations && ((() => {
            const archivedTotalItems = sortedArchivedRegistrations.length;
            const archivedTotalPages = Math.ceil(archivedTotalItems / archivedItemsPerPage);
            const archivedStartIndex = (archivedCurrentPage - 1) * archivedItemsPerPage;
            const archivedEndIndex = archivedStartIndex + archivedItemsPerPage;
            const paginatedArchivedRegistrations = sortedArchivedRegistrations.slice(archivedStartIndex, archivedEndIndex);
            
            const handleArchivedPageChange = (page: number) => {
              setArchivedCurrentPage(page);
            };

            return (
            <div style={{ 
              border: '1px solid #e5e7eb', 
              borderTop: 'none',
              borderRadius: '0 0 8px 8px',
              overflowX: 'auto'
            }}>
              <table className="admin-table" style={{ margin: 0 }}>
                <thead>
                  <tr>
                    <th onClick={() => handleSort('registrationDate')} style={{ cursor: 'pointer' }}>
                      Date {sortField === 'registrationDate' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('firstName')} style={{ cursor: 'pointer' }}>
                      Participant {sortField === 'firstName' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th>Course</th>
                    <th onClick={() => handleSort('company')} style={{ cursor: 'pointer' }}>
                      Company {sortField === 'company' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('email')} style={{ cursor: 'pointer' }}>
                      Email {sortField === 'email' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('phone')} style={{ cursor: 'pointer' }}>
                      Phone {sortField === 'phone' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>
                      Status {sortField === 'status' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedArchivedRegistrations.map((registration) => {
                    const course = courses.find(c => c.id === registration.courseId);
                    const statusStyle = getStatusColor(registration.status);
                    
                    return (
                      <tr key={registration.id} style={{ opacity: 0.7, minHeight: '60px' }}>
                        <td style={{ verticalAlign: 'middle', padding: '12px 16px' }}>
                          <div style={{ 
                            fontSize: '12px', 
                            fontWeight: 500,
                            color: '#6b7280',
                            textAlign: 'center'
                          }}>
                            {new Date(registration.registrationDate).toLocaleDateString('de-DE', {
                              day: '2-digit',
                              month: '2-digit',
                              year: '2-digit'
                            })}
                          </div>
                        </td>
                        <td style={{ verticalAlign: 'middle', padding: '12px 16px' }}>
                          <div style={{ fontWeight: 600, color: '#1f2937' }}>
                            {registration.firstName} {registration.lastName}
                          </div>
                        </td>
                        <td style={{ verticalAlign: 'middle', padding: '12px 16px' }}>
                          <div>
                            <div style={{ fontWeight: 500 }}>
                              {course?.title || 'Unknown Course'}
                            </div>
                            {course?.startDate && (
                              <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                📅 {course.startDate} - {course.endDate}
                              </div>
                            )}
                            {course?.location && (
                              <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                📍 {course.location}
                              </div>
                            )}
                          </div>
                        </td>
                        <td style={{ verticalAlign: 'middle', padding: '12px 16px' }}>
                          <div>
                            <div style={{ fontWeight: 500 }}>
                              {registration.company || '-'}
                            </div>
                            {(registration.registrantFirstName || registration.registrantLastName) && (
                              <div style={{ 
                                fontSize: '12px', 
                                color: '#6b7280', 
                                marginTop: '2px' 
                              }}>
                                Registrant: {registration.registrantFirstName} {registration.registrantLastName}
                              </div>
                            )}
                          </div>
                        </td>
                        <td style={{ verticalAlign: 'middle', padding: '12px 16px' }}>
                          <div style={{ fontSize: '13px' }}>
                            {registration.email || '-'}
                          </div>
                        </td>
                        <td style={{ verticalAlign: 'middle', padding: '12px 16px' }}>
                          <div style={{ fontSize: '13px' }}>
                            {registration.phone || '-'}
                          </div>
                        </td>
                        <td style={{ verticalAlign: 'middle', padding: '12px 16px' }}>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: 500,
                            backgroundColor: statusStyle.bg,
                            color: statusStyle.color
                          }}>
                            {getStatusDisplayText(registration.status)}
                          </span>
                        </td>
                        <td style={{ verticalAlign: 'middle', padding: '12px 16px' }}>
                          <div className="actions" style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                            {/* First row: Edit button */}
                            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                              {onEditRegistration && (
                                <button
                                  onClick={() => onEditRegistration(registration.id)}
                                  style={{
                                    padding: '4px 8px',
                                    border: '1px solid #008F99',
                                    borderRadius: '4px',
                                    background: 'white',
                                    color: '#008F99',
                                    fontSize: '11px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background = '#008F99';
                                    e.currentTarget.style.color = 'white';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'white';
                                    e.currentTarget.style.color = '#008F99';
                                  }}
                                >
                                  Edit
                                </button>
                              )}
                            </div>
                            
                            {/* Second row: Empty for archived items */}
                            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                              {/* Archived items typically only have edit functionality */}
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              
              {/* Archived Pagination Controls */}
              {archivedTotalPages > 1 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '8px',
                  marginTop: '20px',
                  padding: '16px'
                }}>
                  <button
                    onClick={() => handleArchivedPageChange(archivedCurrentPage - 1)}
                    disabled={archivedCurrentPage === 1}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      background: archivedCurrentPage === 1 ? '#f9fafb' : 'white',
                      color: archivedCurrentPage === 1 ? '#9ca3af' : '#374151',
                      cursor: archivedCurrentPage === 1 ? 'not-allowed' : 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    Previous
                  </button>
                  
                  {Array.from({ length: archivedTotalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => handleArchivedPageChange(page)}
                      style={{
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        background: archivedCurrentPage === page ? '#008F99' : 'white',
                        color: archivedCurrentPage === page ? 'white' : '#374151',
                        cursor: 'pointer',
                        fontSize: '14px',
                        minWidth: '40px'
                      }}
                    >
                      {page}
                    </button>
                  ))}
                  
                  <button
                    onClick={() => handleArchivedPageChange(archivedCurrentPage + 1)}
                    disabled={archivedCurrentPage === archivedTotalPages}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      background: archivedCurrentPage === archivedTotalPages ? '#f9fafb' : 'white',
                      color: archivedCurrentPage === archivedTotalPages ? '#9ca3af' : '#374151',
                      cursor: archivedCurrentPage === archivedTotalPages ? 'not-allowed' : 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    Next
                  </button>
                  
                  <div style={{ marginLeft: '16px', fontSize: '14px', color: '#6b7280' }}>
                    Showing {archivedStartIndex + 1}-{Math.min(archivedEndIndex, archivedTotalItems)} of {archivedTotalItems} archived participants
                  </div>
                </div>
              )}
            </div>
            );
          })())}
        </div>
      )}

      {/* Cancelled Participants */}
      {cancelledRegistrations.length > 0 && (
        <div style={{ marginTop: '24px' }}>
          <div 
            onClick={() => setShowCancelledRegistrations(!showCancelledRegistrations)}
            style={{
              padding: '12px 16px',
              background: '#f8f9fa',
              border: '1px solid #e5e7eb',
              borderRadius: '8px 8px 0 0',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontWeight: '600',
              color: '#374151'
            }}
          >
            <span>Cancelled Participants ({cancelledRegistrations.length})</span>
            <span style={{ fontSize: '12px' }}>
              {showCancelledRegistrations ? '▼' : '▶'}
            </span>
          </div>
          
          {showCancelledRegistrations && ((() => {
            const cancelledTotalItems = sortedCancelledRegistrations.length;
            const cancelledTotalPages = Math.ceil(cancelledTotalItems / cancelledItemsPerPage);
            const cancelledStartIndex = (cancelledCurrentPage - 1) * cancelledItemsPerPage;
            const cancelledEndIndex = cancelledStartIndex + cancelledItemsPerPage;
            const paginatedCancelledRegistrations = sortedCancelledRegistrations.slice(cancelledStartIndex, cancelledEndIndex);
            
            const handleCancelledPageChange = (page: number) => {
              setCancelledCurrentPage(page);
            };

            return (
            <div style={{ 
              border: '1px solid #e5e7eb', 
              borderTop: 'none',
              borderRadius: '0 0 8px 8px',
              overflowX: 'auto'
            }}>
              <table className="admin-table" style={{ margin: 0 }}>
                <thead>
                  <tr>
                    <th onClick={() => handleSort('registrationDate')} style={{ cursor: 'pointer' }}>
                      Date {sortField === 'registrationDate' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('firstName')} style={{ cursor: 'pointer' }}>
                      Participant {sortField === 'firstName' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th>Course</th>
                    <th onClick={() => handleSort('company')} style={{ cursor: 'pointer' }}>
                      Company {sortField === 'company' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('email')} style={{ cursor: 'pointer' }}>
                      Email {sortField === 'email' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('phone')} style={{ cursor: 'pointer' }}>
                      Phone {sortField === 'phone' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>
                      Status {sortField === 'status' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedCancelledRegistrations.map((registration) => {
                    const course = courses.find(c => c.id === registration.courseId);
                    const statusStyle = getStatusColor(registration.status);
                    
                    return (
                      <tr key={registration.id} style={{ opacity: 0.7, minHeight: '60px' }}>
                        <td style={{ verticalAlign: 'middle', padding: '12px 16px' }}>
                          <div style={{ 
                            fontSize: '12px', 
                            fontWeight: 500,
                            color: '#6b7280',
                            textAlign: 'center'
                          }}>
                            {new Date(registration.registrationDate).toLocaleDateString('de-DE', {
                              day: '2-digit',
                              month: '2-digit',
                              year: '2-digit'
                            })}
                          </div>
                        </td>
                        <td style={{ verticalAlign: 'middle', padding: '12px 16px' }}>
                          <div style={{ fontWeight: 600, color: '#1f2937' }}>
                            {registration.firstName} {registration.lastName}
                          </div>
                        </td>
                        <td style={{ verticalAlign: 'middle', padding: '12px 16px' }}>
                          <div>
                            <div style={{ fontWeight: 500 }}>
                              {course?.title || 'Unknown Course'}
                            </div>
                            {course?.startDate && (
                              <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                📅 {course.startDate} - {course.endDate}
                              </div>
                            )}
                            {course?.location && (
                              <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                📍 {course.location}
                              </div>
                            )}
                          </div>
                        </td>
                        <td style={{ verticalAlign: 'middle', padding: '12px 16px' }}>
                          <div>
                            <div style={{ fontWeight: 500 }}>
                              {registration.company || '-'}
                            </div>
                            {(registration.registrantFirstName || registration.registrantLastName) && (
                              <div style={{ 
                                fontSize: '12px', 
                                color: '#6b7280', 
                                marginTop: '2px' 
                              }}>
                                Registrant: {registration.registrantFirstName} {registration.registrantLastName}
                              </div>
                            )}
                          </div>
                        </td>
                        <td style={{ verticalAlign: 'middle', padding: '12px 16px' }}>
                          <div style={{ fontSize: '13px' }}>
                            {registration.email || '-'}
                          </div>
                        </td>
                        <td style={{ verticalAlign: 'middle', padding: '12px 16px' }}>
                          <div style={{ fontSize: '13px' }}>
                            {registration.phone || '-'}
                          </div>
                        </td>
                        <td style={{ verticalAlign: 'middle', padding: '12px 16px' }}>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: 500,
                            backgroundColor: statusStyle.bg,
                            color: statusStyle.color
                          }}>
                            {getStatusDisplayText(registration.status)}
                          </span>
                        </td>
                        <td style={{ verticalAlign: 'middle', padding: '12px 16px' }}>
                          <div className="actions" style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                            {/* First row: Edit button */}
                            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                              {onEditRegistration && (
                                <button
                                  onClick={() => onEditRegistration(registration.id)}
                                  style={{
                                    padding: '4px 8px',
                                    border: '1px solid #008F99',
                                    borderRadius: '4px',
                                    background: 'white',
                                    color: '#008F99',
                                    fontSize: '11px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background = '#008F99';
                                    e.currentTarget.style.color = 'white';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'white';
                                    e.currentTarget.style.color = '#008F99';
                                  }}
                                >
                                  Edit
                                </button>
                              )}
                            </div>
                            
                            {/* Second row: Empty for cancelled items */}
                            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                              {/* Cancelled items typically only have edit functionality */}
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              
              {/* Cancelled Pagination Controls */}
              {cancelledTotalPages > 1 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '8px',
                  marginTop: '20px',
                  padding: '16px'
                }}>
                  <button
                    onClick={() => handleCancelledPageChange(cancelledCurrentPage - 1)}
                    disabled={cancelledCurrentPage === 1}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      background: cancelledCurrentPage === 1 ? '#f9fafb' : 'white',
                      color: cancelledCurrentPage === 1 ? '#9ca3af' : '#374151',
                      cursor: cancelledCurrentPage === 1 ? 'not-allowed' : 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    Previous
                  </button>
                  
                  {Array.from({ length: cancelledTotalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => handleCancelledPageChange(page)}
                      style={{
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        background: cancelledCurrentPage === page ? '#008F99' : 'white',
                        color: cancelledCurrentPage === page ? 'white' : '#374151',
                        cursor: 'pointer',
                        fontSize: '14px',
                        minWidth: '40px'
                      }}
                    >
                      {page}
                    </button>
                  ))}
                  
                  <button
                    onClick={() => handleCancelledPageChange(cancelledCurrentPage + 1)}
                    disabled={cancelledCurrentPage === cancelledTotalPages}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      background: cancelledCurrentPage === cancelledTotalPages ? '#f9fafb' : 'white',
                      color: cancelledCurrentPage === cancelledTotalPages ? '#9ca3af' : '#374151',
                      cursor: cancelledCurrentPage === cancelledTotalPages ? 'not-allowed' : 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    Next
                  </button>
                  
                  <div style={{ marginLeft: '16px', fontSize: '14px', color: '#6b7280' }}>
                    Showing {cancelledStartIndex + 1}-{Math.min(cancelledEndIndex, cancelledTotalItems)} of {cancelledTotalItems} cancelled participants
                  </div>
                </div>
              )}
            </div>
            );
          })())}
        </div>
      )}
      
      {/* Blank footer */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: '280px',
        right: 0,
        background: 'white',
        borderTop: '1px solid #e5e7eb',
        boxShadow: '0 -4px 10px rgba(0, 0, 0, 0.06)',
        zIndex: 200,
        padding: '12px 24px',
        height: '48px'
      }} />
    </div>
  );
};

export default AdminRegistrations;
